import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus, Search, LayoutGrid, List, FolderKanban, UsersRound } from 'lucide-react'
import { useState } from 'react'

import {
  EmptyState,
  NoResultsEmptyState,
  determineEmptyStateType,
} from '@/components/common/empty-state'
import {
  ResponsiveListContainer,
  ListHeader,
  FilterBar,
  SearchBox,
  ResponsiveGrid,
  ResponsiveTable,
} from '@/components/ui/responsive-list'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CardGridSkeleton, TableRowsSkeleton } from '@/components/ui/skeleton'
import { useDebouncedValue , useTeams } from '@/hooks'
import { useProjects, type ProjectStatus, type ProjectListParams } from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 项目列表页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/')({
  component: ProjectListPage,
})

const STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: '规划中',
  active: '进行中',
  completed: '已完成',
  archived: '已归档',
}

const STATUS_COLORS: Record<ProjectStatus, string> = {
  planning: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  archived: 'bg-yellow-100 text-yellow-800',
}

function ProjectListPage() {
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const [filters, setFilters] = useState<ProjectListParams>({
    page: 1,
    pageSize: 20,
  })
  const [searchInput, setSearchInput] = useState('')
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')

  // 使用防抖搜索
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  const { data: projects, isLoading, error } = useProjects({
    ...filters,
    search: debouncedSearch || undefined,
  })
  const { data: teams } = useTeams({ status: 'active' })

  const handleStatusFilter = (status: ProjectStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }

  const handleTeamFilter = (teamId: string) => {
    setSelectedTeamId(teamId)
  }

  // 前端过滤（如果后端支持 teamId 参数，可以移除这个）
  const filteredProjects = selectedTeamId && projects
    ? projects.filter((p) => (p as { teamId?: string }).teamId === selectedTeamId)
    : projects

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <ResponsiveListContainer>
      {/* 页面标题 */}
      <ListHeader
        title="项目管理"
        description="管理和跟踪所有项目"
        actions={
          <Link
            to="/pm/projects/new"
            search={{ teamId: undefined }}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">新建项目</span>
            <span className="sm:hidden">新建</span>
          </Link>
        }
      />

      {/* 筛选和搜索 */}
      <FilterBar>
        {/* 搜索框 */}
        <SearchBox>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="搜索项目..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </SearchBox>

        {/* 团队筛选 */}
        {teams && teams.length > 0 && (
          <div className="flex items-center gap-2">
            <UsersRound className="h-4 w-4 text-muted-foreground hidden sm:block" />
            <Select
              value={selectedTeamId || 'all'}
              onValueChange={(value) => handleTeamFilter(value === 'all' ? '' : value)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="全部团队" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部团队</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 状态筛选 - 移动端使用下拉框 */}
        <div className="flex items-center gap-2 sm:hidden">
          <Select
            value={filters.status ?? 'all'}
            onValueChange={(value) => handleStatusFilter(value === 'all' ? undefined : value as ProjectStatus)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="全部状态" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部状态</SelectItem>
              {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((status) => (
                <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 状态筛选 - 桌面端使用按钮组 */}
        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              !filters.status
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            全部
          </button>
          {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filters.status === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              )}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>

        {/* 视图切换 */}
        <div className="flex items-center gap-1 rounded-md border p-1 ml-auto">
          <button
            onClick={() => setViewMode('card')}
            className={cn(
              'rounded p-1.5 transition-colors',
              viewMode === 'card' ? 'bg-muted' : 'hover:bg-muted/50'
            )}
            title="卡片视图"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              'rounded p-1.5 transition-colors',
              viewMode === 'list' ? 'bg-muted' : 'hover:bg-muted/50'
            )}
            title="列表视图"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </FilterBar>

      {/* 项目列表 */}
      {isLoading ? (
        viewMode === 'card' ? (
          <CardGridSkeleton count={8} />
        ) : (
          <TableRowsSkeleton rows={5} columns={5} />
        )
      ) : !filteredProjects || filteredProjects.length === 0 ? (
        // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
        (() => {
          const hasFilters = Boolean(filters.status || selectedTeamId || debouncedSearch)
          const emptyStateType = determineEmptyStateType({
            hasData: false,
            hasFilters,
            hasError: false,
            isFirstVisit: !projects || projects.length === 0,
          })
          
          if (emptyStateType === 'no-results') {
            return (
              <NoResultsEmptyState
                title="未找到匹配的项目"
                description="尝试调整筛选条件或搜索关键词"
                onClearFilters={() => {
                  setFilters({ page: 1, pageSize: 20 })
                  setSelectedTeamId('')
                  setSearchInput('')
                }}
              />
            )
          }
          
          return (
            <EmptyState
              type={emptyStateType}
              title="暂无项目"
              description="点击上方按钮创建第一个项目"
              icon={FolderKanban}
              primaryAction={{
                label: '新建项目',
                onClick: () => window.location.href = '/pm/projects/new',
              }}
            />
          )
        })()
      ) : viewMode === 'card' ? (
        <ResponsiveGrid columns={4}>
          {filteredProjects.map((project) => (
            <Link
              key={project.id}
              to="/pm/projects/$projectId"
              params={{ projectId: project.id }}
              className="group rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">{project.code}</p>
                  <h3 className="mt-1 truncate font-semibold group-hover:text-primary">
                    {project.name}
                  </h3>
                </div>
                <span
                  className={cn(
                    'ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                    STATUS_COLORS[project.status]
                  )}
                >
                  {STATUS_LABELS[project.status]}
                </span>
              </div>
              {project.description && (
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                  {project.description}
                </p>
              )}
              <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                {project.startDate && (
                  <span className="hidden sm:inline">开始: {new Date(project.startDate).toLocaleDateString('zh-CN')}</span>
                )}
                {project.endDate && (
                  <span>截止: {new Date(project.endDate).toLocaleDateString('zh-CN')}</span>
                )}
              </div>
            </Link>
          ))}
        </ResponsiveGrid>
      ) : (
        <ResponsiveTable>
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium">项目编号</th>
                <th className="px-4 py-3 text-left text-sm font-medium">项目名称</th>
                <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden md:table-cell">开始日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium hidden sm:table-cell">截止日期</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => (
                <tr key={project.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm">{project.code}</td>
                  <td className="px-4 py-3">
                    <Link
                      to="/pm/projects/$projectId"
                      params={{ projectId: project.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {project.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_COLORS[project.status]
                      )}
                    >
                      {STATUS_LABELS[project.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                    {project.startDate
                      ? new Date(project.startDate).toLocaleDateString('zh-CN')
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                    {project.endDate
                      ? new Date(project.endDate).toLocaleDateString('zh-CN')
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>
      )}
    </ResponsiveListContainer>
  )
}
